import type { ReactNode } from "react";

import type {
  BoxProps,
  StackProps,
} from "@braintrust/braintrust-ui-components";
import {
  Box,
  IconButton,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  CircleCheckIcon,
  EditPenIcon,
  ErrorSVGIcon,
  WarningIcon,
} from "@braintrust/braintrust-ui-components/Icons";

import styles from "./style.module.scss";

export const ReviewBeforeSendingSummaryBox = (props: StackProps) => {
  return (
    <Stack
      className={styles.reviewBeforeSendingSummary}
      {...props}
      sx={{
        border: "1px solid #EFEFEE",
        boxShadow: "0px 1px 2px 0px #1414170A",
        ...props?.sx,
      }}
    />
  );
};

export type ReviewBeforeSendingSummaryItemProps = {
  sectionName: string;
  sectionContent: ReactNode;
  ellipsis?: boolean;
  isError?: boolean;
  isWarning?: boolean;
  onEditClick?: () => void;
} & Pick<BoxProps, "sx">;

export const ReviewBeforeSendingSummaryItem = ({
  sectionName,
  sectionContent,
  ellipsis = true,
  isError,
  isWarning,
  onEditClick,
  ...containerProps
}: ReviewBeforeSendingSummaryItemProps) => {
  return (
    <Box className={styles.reviewBeforeSendingSummaryItem} {...containerProps}>
      <Box
        sx={{
          display: "flex",
          maxWidth: "90%",
          flexDirection: "column",
          alignSelf: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <Typography
            component="p"
            variant="label"
            sx={{ pr: 1, minWidth: "fit-content" }}
            size="medium"
          >
            {sectionName}
          </Typography>
          {!!sectionContent && !isError && (
            <Typography
              component="div"
              variant="paragraph"
              ellipsis={ellipsis}
              sx={{ mt: "2px", overflow: "hidden" }}
              size="small"
            >
              {sectionContent}
            </Typography>
          )}
          {isError && <MissingFieldErrorMessage />}
        </Box>
      </Box>
      {isWarning ? (
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            flexShrink: 0,
            width: "22px",
            height: "22px",
            background: "#E8B50033",
          }}
        >
          <WarningIcon
            sx={{
              fontSize: 14,
              color: "#B8830A",
              path: { strokeWidth: "4" },
            }}
          />
        </Box>
      ) : !!onEditClick ? (
        <IconButton
          variant="black-outlined"
          size="x-small"
          aria-label="Edit field"
          onClick={onEditClick}
        >
          <EditPenIcon />
        </IconButton>
      ) : (
        <CircleCheckIcon
          sx={{
            mt: "5px",
            color: "var(--positive-2)",
            fontSize: 16,
          }}
        />
      )}
    </Box>
  );
};

const MissingFieldErrorMessage = () => (
  <Typography
    component="p"
    variant="paragraph"
    color="negative"
    ellipsis={true}
    sx={{
      mr: "8px",
      mt: "2px",
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
    }}
    size="small"
  >
    <ErrorSVGIcon
      style={{
        color: "var(--negative-2)",
        width: "18px",
        height: "18px",
      }}
    />
    Missing required field
  </Typography>
);
